import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import api from '../../utils/api';

/**
 * Unified dashboard that renders different content based on user role
 */
function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();

  if (!user) return null;

  // Render role-specific dashboard content
  switch (user.role) {
    case 'admin':
      return <AdminDashboardContent />;
    case 'owner':
      return <OwnerDashboardContent />;
    case 'vendor':
      return <VendorDashboardContent />;
    default:
      return <div className="card">{t('common.unknownRole')}</div>;
  }
}

/**
 * Admin dashboard content - stats, top properties/offers
 */
function AdminDashboardContent() {
  const { t } = useI18n();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const data = await api.get('/admin/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleExportCSV() {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/admin/export/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `events-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return <LoadingSpinner size={32} />;
  }

  if (!dashboard) return null;

  return (
    <>
      {/* Stats */}
      <div className="flex gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 45%', textAlign: 'center', minWidth: '140px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--primary)' }}>
            {dashboard.stats.properties}
          </div>
          <div className="text-secondary text-sm">{t('admin.totalProperties')}</div>
        </div>
        <div className="card" style={{ flex: '1 1 45%', textAlign: 'center', minWidth: '140px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {dashboard.stats.offers}
          </div>
          <div className="text-secondary text-sm">{t('admin.totalOffers')}</div>
        </div>
        <div className="card" style={{ flex: '1 1 45%', textAlign: 'center', minWidth: '140px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {dashboard.stats.vendors}
          </div>
          <div className="text-secondary text-sm">{t('admin.totalVendors')}</div>
        </div>
        <div className="card" style={{ flex: '1 1 45%', textAlign: 'center', minWidth: '140px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {dashboard.stats.owners}
          </div>
          <div className="text-secondary text-sm">{t('admin.totalOwners')}</div>
        </div>
      </div>

      {/* Last 30 days */}
      <div className="card mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 style={{ fontSize: '14px', margin: 0 }}>{t('admin.last30Days')}</h3>
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="btn btn-secondary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
          >
            {exporting ? t('common.exporting') : t('admin.exportCSV')}
          </button>
        </div>
        <div className="flex justify-between">
          <div className="text-center">
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{dashboard.last30Days.scans}</div>
            <div className="text-secondary text-xs">{t('admin.scans')}</div>
          </div>
          <div className="text-center">
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{dashboard.last30Days.clicks}</div>
            <div className="text-secondary text-xs">{t('admin.clicks')}</div>
          </div>
          <div className="text-center">
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--success)' }}>{dashboard.last30Days.redemptions}</div>
            <div className="text-secondary text-xs">{t('admin.redemptions')}</div>
          </div>
        </div>
      </div>

      {/* Top Properties & Offers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div className="card">
          <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('admin.topProperties')}</h3>
          {dashboard.topProperties?.length > 0 ? (
            <div>
              {dashboard.topProperties.map((prop, idx) => (
                <div key={prop.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < dashboard.topProperties.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{prop.name}</div>
                    <div className="text-xs text-secondary">{prop.unit_code}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--primary)' }}>
                    {prop.event_count}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary text-sm">{t('admin.noActivity')}</p>
          )}
        </div>

        <div className="card">
          <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('admin.topOffers')}</h3>
          {dashboard.topOffers?.length > 0 ? (
            <div>
              {dashboard.topOffers.map((offer, idx) => (
                <div key={offer.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < dashboard.topOffers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{offer.title}</div>
                    <div className="text-xs text-secondary">{offer.vendor_name}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--primary)' }}>
                    {offer.event_count}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary text-sm">{t('admin.noActivity')}</p>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Owner dashboard content - property list with add/delete, search and sort
 */
function OwnerDashboardContent() {
  const { t } = useI18n();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ unit_code: '', name: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadProperties();
    async function loadProperties() {
      try {
        const data = await api.get('/owner/properties');
        setProperties(data);
      } catch (err) {
        console.error('Failed to load properties:', err);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  async function refreshProperties() {
    try {
      const data = await api.get('/owner/properties');
      setProperties(data);
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/owner/properties', form);
      setShowForm(false);
      setForm({ unit_code: '', name: '', address: '' });
      refreshProperties();
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(t('owner.confirmDelete', { name }))) return;
    try {
      await api.delete(`/owner/properties/${id}`);
      refreshProperties();
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    }
  }

  // Filter and sort properties
  const filteredProperties = properties
    .filter(p => {
      if (!search) return true;
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) || p.unit_code.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'code') return a.unit_code.localeCompare(b.unit_code);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at); // newest
    });

  if (loading) {
    return <LoadingSpinner size={32} />;
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 style={{ fontSize: '16px', margin: 0 }}>{t('owner.myProperties')}</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
          style={{ padding: '8px 16px', fontSize: '14px' }}
        >
          {showForm ? t('common.cancel') : `+ ${t('owner.addProperty')}`}
        </button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('owner.newProperty')}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">{t('admin.unitCode')} *</label>
              <input
                type="text"
                className="input"
                value={form.unit_code}
                onChange={(e) => setForm(f => ({ ...f, unit_code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') }))}
                required
                placeholder="SUNSET-101"
                style={{ textTransform: 'uppercase' }}
              />
              <p className="text-secondary text-xs mt-1">{t('admin.unitCodeHelp')}</p>
            </div>
            <div className="form-group">
              <label className="label">{t('admin.propertyName')} *</label>
              <input
                type="text"
                className="input"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="Sunset Beach Villa"
              />
            </div>
            <div className="form-group">
              <label className="label">{t('common.address')}</label>
              <input
                type="text"
                className="input"
                value={form.address}
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Beach Road, Pattaya"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
              {saving ? t('common.saving') : t('owner.createProperty')}
            </button>
          </form>
        </div>
      )}

      {/* Search and Sort - only show if there are properties */}
      {properties.length > 0 && (
        <div className="card mb-4" style={{ padding: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
            <input
              type="text"
              className="input"
              placeholder={t('common.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ margin: 0 }}
            />
            <select
              className="input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ margin: 0, width: 'auto' }}
            >
              <option value="newest">{t('admin.newestFirst')}</option>
              <option value="oldest">{t('admin.oldestFirst')}</option>
              <option value="name">{t('admin.nameAZ')}</option>
              <option value="code">{t('admin.unitCode')}</option>
            </select>
          </div>
        </div>
      )}

      {properties.length === 0 && !showForm ? (
        <div className="card text-center text-secondary">
          <p>{t('owner.noProperties')}</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary mt-3"
          >
            + {t('owner.addProperty')}
          </button>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="card text-center text-secondary">
          {t('common.noResults')}
        </div>
      ) : (
        filteredProperties.map(property => (
          <div key={property.id} className="card" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Link
              to={`/properties/${property.id}`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: '16px', alignItems: 'center', flex: 1 }}
            >
              {property.logo_url ? (
                <img
                  src={property.logo_url}
                  alt=""
                  style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '8px',
                  background: 'var(--background)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  🏠
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', marginBottom: '4px' }}>{property.name}</h3>
                <p className="text-secondary text-sm">{property.unit_code}</p>
              </div>
              <span style={{ color: 'var(--text-secondary)' }}>→</span>
            </Link>
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(property.id, property.name); }}
              className="btn btn-secondary"
              style={{ padding: '6px 10px', fontSize: '12px', color: 'var(--error)' }}
            >
              {t('common.delete')}
            </button>
          </div>
        ))
      )}
    </>
  );
}

/**
 * Vendor dashboard content - stats and quick access to redemptions
 */
function VendorDashboardContent() {
  const { t } = useI18n();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const data = await api.get('/vendor/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingSpinner size={32} />;
  }

  return (
    <>
      {/* Stats */}
      <div className="flex gap-3 mb-4">
        <div className="card" style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--primary)' }}>
            {dashboard?.thisMonth || 0}
          </div>
          <div className="text-secondary text-sm">{t('vendor.redemptionsThisMonth')}</div>
        </div>
        <div className="card" style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
            {dashboard?.lifetime || 0}
          </div>
          <div className="text-secondary text-sm">{t('vendor.lifetimeRedemptions')}</div>
        </div>
      </div>

      {/* Quick link to redemptions */}
      <Link to="/redemptions" style={{ textDecoration: 'none' }}>
        <button className="btn btn-primary btn-block mb-4" style={{ padding: '16px' }}>
          {t('vendor.scanCoupon')}
        </button>
      </Link>

      {/* Recent redemptions */}
      {dashboard?.recentRedemptions?.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>{t('vendor.recentRedemptions')}</h3>
          {dashboard.recentRedemptions.map((r, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: i < dashboard.recentRedemptions.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div className="flex justify-between">
                <span className="text-sm">{r.offer_title}</span>
                <span className="text-secondary text-xs">{new Date(r.redeemed_at).toLocaleDateString()}</span>
              </div>
              <span className="text-secondary text-xs">{r.property_name}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default Dashboard;
